/**
 * GuestPortalPage — /guest/:token
 * The external worker's list of everything delegated to them: open work
 * orders (Ärenden), open projects (Projekt) and a collapsed "Nyligen
 * slutförda" (30 days, read-only). Rows open the detail pages under the same
 * token. The förvaltare of automatisk tilldelning (#1653) also gets
 * "Felanmälningar att bedöma" at the top. A refusal renders the parked page
 * for the link's state.
 */
import React, { useCallback, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Badge, Container } from 'react-bootstrap';
import { Briefcase, ChevronDown, ChevronRight, ClipboardList, Clock } from 'lucide-react';
import { PageLoader } from '../../Components/LoadingSpinner';
import { guestApi } from '../../api/guestApi';
import { PortalCompleted, PortalData, PortalProject, PortalWorkOrder } from '../../types/guestAccess';
import { formatDateForDisplay } from '../../Helpers/DateUtils';
import { expiryWarningFor } from '../../utils/linkValidity';
import { WORK_ORDER_LABELS } from '../WorkOrders/workOrderSections';
import { workOrderStatusBadge } from './components/GuestDelmomentSection';
import { GuestInvalidLinkPage, GuestLinkRefusalPage, GuestLoadingPage, GuestUnavailablePage } from './components/GuestLinkStatePages';
import GuestIssueAssessmentSection from './components/GuestIssueAssessmentSection';
import { useGuestFetch } from './hooks/useGuestFetch';
import logoWhite from '../../Images/logo_white.png';
import './GuestTaskPage.css';

const PortalSection: React.FC<{ icon: React.ReactNode; title: string; count: number; empty: string; children: React.ReactNode }> = ({
    icon, title, count, empty, children,
}) => (
    <section className="guest-portal__section mb-4" aria-label={title}>
        <h6 className="d-flex align-items-center gap-2 mb-2">
            {icon}
            <span>{title}</span>
            {count > 0 && <Badge bg="secondary">{count}</Badge>}
        </h6>
        {count === 0 ? (
            <p className="text-muted small mb-0">{empty}</p>
        ) : (
            <div className="list-group">{children}</div>
        )}
    </section>
);

const PortalRow: React.FC<{ to: string; title: string; subtitle: string; children: React.ReactNode }> = ({ to, title, subtitle, children }) => (
    <Link to={to} className="list-group-item list-group-item-action guest-portal__row d-flex align-items-center justify-content-between gap-2">
        <span className="d-flex flex-column text-truncate">
            <span className="text-truncate">{title}</span>
            <small className="text-muted text-truncate">{subtitle}</small>
        </span>
        <span className="d-flex align-items-center gap-2 flex-shrink-0">
            {children}
            <ChevronRight size={18} className="text-muted" />
        </span>
    </Link>
);

const WorkOrderRow: React.FC<{ token: string; item: PortalWorkOrder }> = ({ token, item }) => {
    const badge = workOrderStatusBadge(item.status);
    return (
        <PortalRow to={`/guest/${token}/wo/${item.key}`} title={item.title} subtitle={item.buildingName}>
            {item.dueDate && (
                <small className="text-muted">{`${WORK_ORDER_LABELS.dueDate} ${formatDateForDisplay(item.dueDate)}`}</small>
            )}
            <Badge bg={badge.variant}>{badge.label}</Badge>
        </PortalRow>
    );
};

const ProjectRow: React.FC<{ token: string; item: PortalProject }> = ({ token, item }) => (
    <PortalRow to={`/guest/${token}/project/${item.key}`} title={item.name} subtitle={item.buildingName}>
        {item.status && <Badge bg="primary">{item.status}</Badge>}
    </PortalRow>
);

const CompletedRow: React.FC<{ token: string; item: PortalCompleted }> = ({ token, item }) => (
    <PortalRow
        to={item.type === 'workOrder' ? `/guest/${token}/wo/${item.key}` : `/guest/${token}/project/${item.key}`}
        title={item.title}
        subtitle={item.buildingName}
    >
        <small className="text-muted">{`Slutfört ${formatDateForDisplay(item.completedAt)}`}</small>
        <Badge bg="light" text="dark" className="border">{item.type === 'workOrder' ? WORK_ORDER_LABELS.entity : 'Projekt'}</Badge>
    </PortalRow>
);

const PortalContent: React.FC<{ token: string; data: PortalData; onReload: () => Promise<void> }> = ({ token, data, onReload }) => {
    const [showCompleted, setShowCompleted] = useState(false);
    const completed = data.recentlyCompleted;
    const expiresAt = data.link.kind === 'expiring' ? data.link.expiresAt : undefined;
    // Expiry ends access, so the worker is warned while there is time to ask the admin.
    const warning = expiresAt ? expiryWarningFor(expiresAt) : undefined;

    return (
        <div className="guest-page">
            <div className="guest-page__header">
                <img src={logoWhite} alt="Molnfastigheter" className="guest-page__header-logo" />
            </div>
            <Container className="guest-page__container">
                <h4 className="mb-1">{data.user.firstName ? `Hej ${data.user.firstName}!` : 'Mina uppgifter'}</h4>
                {expiresAt && (warning ? (
                    <p className="small mb-4 text-warning-emphasis">
                        {`Länken gäller till ${formatDateForDisplay(expiresAt)} och ${warning}. Kontakta förvaltaren om du behöver den längre.`}
                    </p>
                ) : (
                    <p className="text-muted small mb-4">{`Länken gäller till ${formatDateForDisplay(expiresAt)}`}</p>
                ))}

                {data.issueAssessment && (
                    <GuestIssueAssessmentSection token={token} assessment={data.issueAssessment} onChanged={onReload} />
                )}

                <PortalSection icon={<ClipboardList size={18} />} title="Ärenden" count={data.workOrders.length} empty="Inga öppna ärenden.">
                    {data.workOrders.map((item) => <WorkOrderRow key={item.key} token={token} item={item} />)}
                </PortalSection>

                <PortalSection icon={<Briefcase size={18} />} title="Projekt" count={data.projects.length} empty="Inga öppna projekt.">
                    {data.projects.map((item) => <ProjectRow key={item.key} token={token} item={item} />)}
                </PortalSection>

                <section className="guest-portal__section mb-4" aria-label="Nyligen slutförda">
                    <button
                        type="button"
                        className="guest-portal__toggle d-flex align-items-center gap-2 w-100 text-start"
                        aria-expanded={showCompleted}
                        onClick={() => setShowCompleted((v) => !v)}
                    >
                        <Clock size={18} />
                        <span className="fw-semibold">Nyligen slutförda</span>
                        {completed.length > 0 && <Badge bg="secondary">{completed.length}</Badge>}
                        <span className="ms-auto">{showCompleted ? <ChevronDown size={18} /> : <ChevronRight size={18} />}</span>
                    </button>
                    {showCompleted && (
                        completed.length === 0
                            ? <p className="text-muted small mb-0 mt-2">Inget slutfört de senaste 30 dagarna.</p>
                            : <div className="list-group mt-2">{completed.map((item) => <CompletedRow key={`${item.type}-${item.key}`} token={token} item={item} />)}</div>
                    )}
                </section>

                <div className="guest-page__footer">
                    <small className="text-muted">Powered by Molnfastigheter Sverige AB</small>
                </div>
            </Container>
        </div>
    );
};

const GuestPortalPage: React.FC = () => {
    const { token } = useParams<{ token: string }>();
    const { data, loading, refusal, error } = useGuestFetch(() => guestApi.getPortal(token as string), token ?? null);
    // A re-read after an action replaces the data in place, without the page loader.
    const [reloaded, setReloaded] = useState<PortalData | null>(null);
    const reload = useCallback(async () => {
        if (!token) return;
        try {
            setReloaded(await guestApi.getPortal(token));
        } catch (err) {
            console.error('Portal reload failed', err);
        }
    }, [token]);

    if (loading) {
        return (
            <GuestLoadingPage>
                <PageLoader text="Laddar..." />
            </GuestLoadingPage>
        );
    }
    if (refusal && token) {
        return <GuestLinkRefusalPage token={token} refusal={refusal} />;
    }
    if (!token) {
        return <GuestInvalidLinkPage />;
    }
    if (!data) {
        return <GuestUnavailablePage title="Kunde inte ladda dina uppgifter" text={error ?? undefined} />;
    }
    return <PortalContent token={token} data={reloaded ?? data} onReload={reload} />;
};

export default GuestPortalPage;
